import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentActionmodulesQueryKey,
  useGetApiCompaniesCurrentActionmodulesTemplates,
} from '@/api/generated/company-management-user/company-management-user'
import { ActionModuleKind } from '@/api/model'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { ActionCard } from './ActionCard'
import type { ActionModule } from '../types'

const GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'

/** "Vorlagen" tab: company templates and — for super admins only — global templates, each as its own grid. */
export function ActionModuleTemplates({
  search,
  isSuperAdmin,
  companyId,
  onEdit,
}: {
  search: string
  isSuperAdmin: boolean
  companyId?: number
  onEdit: (m: ActionModule) => void
}) {
  const { t } = useTranslation('action-modules')
  const queryClient = useQueryClient()
  // The backend includes global templates only for super admins (derived from the caller).
  const { data, isLoading, isError } = useGetApiCompaniesCurrentActionmodulesTemplates()

  const { companyTemplates, globalTemplates } = useMemo(() => {
    const q = search.toLowerCase().trim()
    const templates = (data?.data ?? [])
      .filter(m => !q || (m.name ?? '').toLowerCase().includes(q) || (m.description ?? '').toLowerCase().includes(q))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    return {
      companyTemplates: templates.filter(m => m.kind === ActionModuleKind.CompanyTemplate),
      globalTemplates: templates.filter(m => m.kind === ActionModuleKind.GlobalTemplate),
    }
  }, [data, search])

  // A template card creates a live module → refresh the "Aktionsmodule" tab.
  const onLiveModuleCreated = () =>
    queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentActionmodulesQueryKey() })

  const section = (title: string, modules: ActionModule[]) => (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <LoaderGeneral
        isLoading={isLoading}
        isError={isError}
        errorMessage={t('templates.loadError')}
        isEmpty={modules.length === 0}
        emptyMessage={t('templates.empty')}
      >
        <div className={GRID_CLASS}>
          {modules.map(module => (
            <ActionCard
              key={module.id}
              module={module}
              canCopy
              isAdmin={isSuperAdmin}
              companyId={companyId}
              onEdit={onEdit}
              onSaved={onLiveModuleCreated}
            />
          ))}
        </div>
      </LoaderGeneral>
    </section>
  )

  return (
    <div className="flex flex-col gap-8">
      {section(t('templates.companyTitle'), companyTemplates)}
      {isSuperAdmin && section(t('templates.globalTitle'), globalTemplates)}
    </div>
  )
}
